import { InformationCircleIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { Tooltip } from '@/components/ui/Tooltip'

const ValueSkeleton = ({ chars = '3' }) => {
  const getWidthClass = (charCount) => {
    if (charCount <= 1) return 'w-2'
    if (charCount <= 3) return 'w-6'
    if (charCount <= 5) return 'w-10'
    if (charCount <= 8) return 'w-16'
    if (charCount <= 12) return 'w-24'
    if (charCount <= 16) return 'w-32'
    if (charCount <= 20) return 'w-40'
    return 'w-48' // 21+ chars
  }

  return (
    <div
      className={`bg-midnight-light/25 my-1.5 h-5 ${getWidthClass(
        chars,
      )} animate-pulse rounded-sm`}
    ></div>
  )
}

ValueSkeleton.propTypes = {
  chars: PropTypes.string,
}

const MetricCard = ({
  label,
  value,
  defaultValue = '0',
  skeletonWidth = '3',
  loading = false,
  size = 'large',
  children,
}) => {
  const isLarge = size === 'large'

  return (
    <div
      className={`bg-midnight col-span-1 row-span-1 flex rounded-lg ${
        isLarge
          ? 'flex-col items-center justify-center px-3 pb-2 pt-3 sm:col-span-1 md:col-span-1 md:row-span-1'
          : 'items-center justify-between gap-6 px-4 py-6 sm:col-span-1'
      }`}
    >
      <span
        className={mergeClassNames(
          'text-brownGray text-xs uppercase leading-4 tracking-[1.5px]',
          children && 'flex flex-row items-center gap-1',
        )}
      >
        {label}
        {children}
      </span>
      {loading ? (
        <ValueSkeleton chars={skeletonWidth} />
      ) : (
        <span className="text-lg font-semibold leading-8 tracking-[-1px]">
          {value || defaultValue}
        </span>
      )}
    </div>
  )
}

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  defaultValue: PropTypes.string,
  skeletonWidth: PropTypes.string,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['large', 'small']),
  children: PropTypes.node,
}

MetricCard.Tooltip = ({ content, ...props }) => (
  <Tooltip
    content={
      <div className="text-white-dark rounded-lg border border-blue-800 bg-blue-800 p-1.5 text-sm normal-case shadow-md">
        {content}
      </div>
    }
    {...props}
  >
    <InformationCircleIcon className="text-brownGray h-5 w-5" />
  </Tooltip>
)

MetricCard.Tooltip.propTypes = {
  // Content can be any renderable React content (string, JSX element, fragment, etc.)
  // Using PropTypes.node instead of PropTypes.string or PropTypes.element to allow maximum flexibility
  content: PropTypes.node.isRequired,
}

export default MetricCard
